import { Suspense, lazy, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, ArrowRight, Sparkles, Cloud, Boxes } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '../../lib/api';
import { HERO_BACKDROP } from '../../lib/imagery';
import { useBrandImage } from '../../lib/BrandImagesContext';

const HeroScene = lazy(() => import('./HeroScene'));
const HeroParticles = lazy(() => import('./HeroParticles'));

const FALLBACK_HERO = {
    eyebrow: 'ENGINEERING DIGITAL TRANSFORMATION',
    title_line_1: 'Advanced software engineering.',
    title_line_2: 'Intelligent systems. Lasting impact.',
    subtitle: 'We design, engineer, integrate, and operate secure digital platforms for governments, enterprises, and ambitious ventures—combining advanced programming, artificial intelligence, cloud, data, cybersecurity, and automation to solve complex operational challenges.',
    primary_cta_label: 'Book a Consultation',
    primary_cta_href: '/contact',
    secondary_cta_label: 'Explore Our Capabilities',
    secondary_cta_href: '/services',
};

function useReducedMotion() {
    const [reduced, setReduced] = useState(false);
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
        const onChange = () => setReduced(mq.matches);
        onChange();
        mq.addEventListener?.('change', onChange);
        return () => mq.removeEventListener?.('change', onChange);
    }, []);
    return reduced;
}

export default function Hero() {
    const reduced = useReducedMotion();
    const [hero, setHero] = useState(FALLBACK_HERO);
    const heroBackdrop = useBrandImage('home.hero.backdrop', HERO_BACKDROP);
    useEffect(() => {
        api.get('/content').then((r) => {
            if (r?.data?.hero) setHero({ ...FALLBACK_HERO, ...r.data.hero });
        }).catch(() => {});
    }, []);
    return (
        <section
            data-testid="home-hero"
            className="relative isolate overflow-hidden pt-28 sm:pt-32 lg:pt-36 pb-20 lg:pb-28"
        >
            {/* Background layers */}
            <div
                aria-hidden
                className="absolute inset-0 -z-20 pointer-events-none"
                style={{
                    backgroundImage: `url(${heroBackdrop})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    filter: 'saturate(0.55) hue-rotate(-8deg) brightness(0.55)',
                    opacity: 0.28,
                }}
            />
            <div
                aria-hidden
                className="absolute inset-0 -z-20 pointer-events-none bg-gradient-to-b from-[rgba(8,12,20,0.9)] via-[rgba(8,12,20,0.75)] to-[rgba(8,12,20,0.95)]"
            />
            <div className="absolute inset-0 -z-10 dt-grid-overlay dt-noise pointer-events-none" />
            {!reduced && (
                <Suspense fallback={null}>
                    <div className="absolute inset-0 -z-10 pointer-events-none opacity-90">
                        <HeroParticles />
                    </div>
                </Suspense>
            )}

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                <div className="lg:col-span-7 relative">
                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, ease: 'easeOut' }}
                        className="dt-section-eyebrow"
                    >
                        <Sparkles className="h-3.5 w-3.5" />
                        {hero.eyebrow}
                    </motion.div>

                    <motion.h1
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: 'easeOut', delay: 0.05 }}
                        className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]"
                    >
                        {hero.title_line_1}
                        <br />
                        <span className="dt-gradient-text">{hero.title_line_2}</span>
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: 'easeOut', delay: 0.12 }}
                        className="mt-5 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed"
                    >
                        {hero.subtitle}
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: 'easeOut', delay: 0.18 }}
                        className="mt-8 flex flex-wrap gap-3"
                    >
                        <Button
                            asChild
                            size="lg"
                            data-testid="hero-primary-cta-button"
                            className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_0_1px_rgba(0,229,255,0.35),0_0_28px_rgba(0,229,255,0.22)]"
                        >
                            <Link to={hero.primary_cta_href || '/contact'} className="inline-flex items-center gap-1.5">
                                {hero.primary_cta_label} <ArrowUpRight className="h-4 w-4" />
                            </Link>
                        </Button>
                        <Button
                            asChild
                            variant="secondary"
                            size="lg"
                            data-testid="hero-secondary-cta-button"
                            className="bg-secondary text-secondary-foreground border border-white/10 hover:border-primary/50 hover:bg-secondary/80"
                        >
                            <Link to={hero.secondary_cta_href || '/services'} className="inline-flex items-center gap-1.5">
                                {hero.secondary_cta_label} <ArrowRight className="h-4 w-4" />
                            </Link>
                        </Button>
                    </motion.div>

                    {/* Trust strip */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.4 }}
                        className="mt-10 flex items-center gap-3 flex-wrap text-xs text-muted-foreground"
                    >
                        <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1">
                            <span className="h-1.5 w-1.5 rounded-full bg-[hsl(152_95%_55%)] dt-pulse-ring" />
                            ENGINEERING • AI • CLOUD • SECURITY
                        </span>
                        <span className="inline-flex items-center gap-2 font-mono">Secure-by-design • Privacy-aware • Responsible AI</span>
                        <span className="inline-flex items-center gap-2 font-mono">Senior-led delivery • End-to-end accountability</span>
                    </motion.div>
                </div>

                {/* 3D scene */}
                <div className="lg:col-span-5 relative h-[420px] sm:h-[460px] lg:h-[520px]">
                    <div className="absolute inset-0 rounded-2xl border border-white/10 bg-[rgba(8,12,20,0.55)] overflow-hidden dt-glow-cyan">
                        <Suspense fallback={null}>
                            {!reduced && <HeroScene />}
                        </Suspense>
                        <div className="absolute inset-0 bg-[radial-gradient(80%_60%_at_50%_50%,transparent_0%,rgba(0,0,0,0.55)_85%)] pointer-events-none" />

                        {/* Floating tech indicators */}
                        <FloatingChip className="top-6 left-6" icon={<Boxes className="h-3.5 w-3.5" />} value="Digital Core" label="Advanced programming" />
                        <FloatingChip className="bottom-8 left-6" icon={<Cloud className="h-3.5 w-3.5" />} value="Scalable Foundations" label="Cloud and data" />
                        <FloatingChip className="top-10 right-6" icon={<Sparkles className="h-3.5 w-3.5" />} value="Intelligent Workflows" label="AI and automation" />
                    </div>
                </div>
            </div>
        </section>
    );
}

function FloatingChip({ className = '', icon, label, value }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className={`absolute ${className} dt-float`}
        >
            <div className="dt-glass-strong rounded-xl px-3 py-2 flex items-center gap-2 text-xs text-foreground/90">
                <span className="text-primary">{icon}</span>
                <span className="font-mono tracking-wide text-foreground">{value}</span>
                <span className="font-mono text-muted-foreground uppercase text-[10px] tracking-widest">{label}</span>
            </div>
        </motion.div>
    );
}
